import { PARTS, type DayWeather, type Sky, type WeatherPart } from '@trippath/shared';
import { addDays, format, parseISO } from 'date-fns';
import { enGB, et } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day as dayLong } from '../../lib/format';
import { SkyIcon } from './SkyIcon';
import { useWeather } from './useWeather';

export const degrees = (value: number) => `${String(Math.round(value))}°`;

/** A day as its button says it: `Mon 8`, `E 8.`. */
function dayButton(iso: string, language: string) {
  return format(parseISO(iso), language === 'et' ? 'EEEEEE d.' : 'EEE d', {
    locale: language === 'et' ? et : enGB,
  });
}

/** Two digits of an hour: `05`. */
export const hour = (at: number) => String(at).padStart(2, '0');

/** `06:10` in hours, `6.17`; `or` when it is not said. */
const hoursOf = (at: string | null, or: number) =>
  at === null ? or : Number(at.slice(0, 2)) + Number(at.slice(3, 5)) / 60;

/** Whether a part of a day, by its hours, is mostly dark: its middle before sunrise or after
 * sunset there, else before six or after six. */
export function isDark(
  from: number,
  to: number,
  sunrise: string | null,
  sunset: string | null,
) {
  const middle = (from + to) / 2;
  return middle < hoursOf(sunrise, 6) || middle > hoursOf(sunset, 18);
}

/** A sky in words: a clear one in the dark is no sunny one. */
export function skyWord(t: (key: string) => string, sky: Sky, night: boolean): string {
  return night && sky === 'sun' ? t('weather.clear') : t(`weather.sky.${sky}`);
}

/** A day's parts with their names and hours, `PARTS` each; one not sent is left out. */
export function partsOf(parts: readonly WeatherPart[]) {
  return PARTS.flatMap(([name, from, to], at) => {
    const part = parts[at];
    return part ? [{ name, from, to, part }] : [];
  });
}

/** A day's parts, night to late evening, each by its hours, sky, temperature and rain. */
export function DayParts({
  parts,
  sunrise,
  sunset,
}: {
  parts: readonly WeatherPart[];
  sunrise: string | null;
  sunset: string | null;
}) {
  const { t } = useTranslation();
  return (
    <ol className="weather-parts" aria-label={t('weather.partsTitle')}>
      {partsOf(parts).map(({ name, from, to, part }) => {
        const night = isDark(from, to, sunrise, sunset);
        return (
          <li key={name}>
            <small>{t(`weather.part.${name}`)}</small>
            <span className="quiet mono">
              {hour(from)}–{hour(to)}
            </span>
            <SkyIcon sky={part.sky} night={night} label={skyWord(t, part.sky, night)} />
            <b>{degrees(part.temp)}</b>
            {part.rainChance !== null && (
              <span className="weather-rain">{String(part.rainChance)} %</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** One known day in full: its sky and temperatures, rain, wind and sun; the usual weather's
 * range, and when a forecast will come. */
function Detail({
  day,
  forecastDays,
  years,
}: {
  day: Extract<DayWeather, { kind: 'was' | 'forecast' | 'usual' }>;
  forecastDays: number;
  years: [number, number];
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const usual = day.usual;
  const sun =
    day.sunrise && day.sunset
      ? t('weather.sunTimes', { from: day.sunrise, to: day.sunset })
      : null;
  return (
    <>
      <div className="weather-day">
        <SkyIcon sky={day.sky} size={48} />
        <div>
          <p className="weather-temps">
            {degrees(day.high)} <span>/ {degrees(day.low)}</span>
          </p>
          <p className="quiet">
            {format(parseISO(day.day), lang === 'et' ? 'EEEE, d. MMMM' : 'EEEE, d MMMM', {
              locale: lang === 'et' ? et : enGB,
            })}{' '}
            · {t(`weather.sky.${day.sky}`)}
          </p>
        </div>
      </div>
      {day.parts.length > 0 && (
        <>
          <h4 className="weather-parts-title">{t('weather.partsTitle')}</h4>
          <DayParts parts={day.parts} sunrise={day.sunrise} sunset={day.sunset} />
        </>
      )}
      <dl className="weather-facts">
        {day.kind === 'was' ? (
          <>
            <dt>{t('weather.rainFell')}</dt>
            <dd>{t('weather.mm', { mm: day.rainMm.toFixed(1) })}</dd>
          </>
        ) : day.kind === 'forecast' ? (
          <>
            <dt>{t('weather.rainChance')}</dt>
            <dd>
              {day.rainChance === null ? '—' : `${String(day.rainChance)} %`} ·{' '}
              {t('weather.mm', { mm: day.rainMm.toFixed(1) })}
            </dd>
          </>
        ) : (
          <>
            <dt>{t('weather.highUsually')}</dt>
            <dd>
              {degrees(day.usual?.highFrom ?? day.high)}–
              {degrees(day.usual?.highTo ?? day.high)}
            </dd>
            <dt>{t('weather.lowUsually')}</dt>
            <dd>
              {degrees(day.usual?.lowFrom ?? day.low)}–{degrees(day.usual?.lowTo ?? day.low)}
            </dd>
            <dt>{t('weather.rainyDays')}</dt>
            <dd>
              {String(day.rainChance ?? 0)} % · {t('weather.mm', { mm: day.rainMm.toFixed(1) })}
            </dd>
          </>
        )}
        <dt>{t('weather.wind')}</dt>
        <dd>{t('weather.kmh', { kmh: Math.round(day.wind) })}</dd>
        {sun && (
          <>
            <dt>{t('weather.sun')}</dt>
            <dd>{sun}</dd>
          </>
        )}
      </dl>
      {usual && (
        <div className="weather-band">
          <p className="quiet">
            {t('weather.band', {
              years: years[1] - years[0] + 1,
              from: degrees(usual.lowFrom),
              to: degrees(usual.highTo),
            })}
          </p>
          <div
            className="weather-band-track"
            role="img"
            aria-label={t('weather.bandLabel', {
              from: degrees(usual.highFrom),
              to: degrees(usual.highTo),
            })}
          >
            {/* The track runs from the night's low to the day's high; what lies below the day's
                usual highs is covered. */}
            <i
              style={{
                width: `${String(
                  ((usual.highFrom - usual.lowFrom) /
                    Math.max(1, usual.highTo - usual.lowFrom)) *
                    100,
                )}%`,
              }}
            />
          </div>
        </div>
      )}
      <p className="quiet weather-note">
        {day.kind === 'was'
          ? t('weather.wasNote')
          : day.kind === 'forecast'
            ? t('weather.forecastNote')
            : t('weather.usualNote', {
                day: dayLong(
                  format(addDays(parseISO(day.day), -(forecastDays - 1)), 'yyyy-MM-dd'),
                  lang,
                ),
                days: forecastDays,
              })}
      </p>
    </>
  );
}

/** A stop's weather on its card (the `Weather` mockup): each day by its sky and high, the day
 * chosen in full. Within sixteen days a forecast, further off the usual weather of the date.
 * Keyed by its stop where it is shown, so another stop starts at its first day. */
export function StopWeather({
  trip,
  stop,
  day = null,
  reveal = false,
}: {
  trip: string;
  stop: string;
  /** The day shown first, `2027-03-10`; the stop's first when it is not one of its days. */
  day?: string | null;
  /** That day brought into view once it is had, as if pressed. */
  reveal?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const state = useWeather(trip);
  const [picked, setPicked] = useState(day);
  // A day pressed is brought into view in full, its parts with it.
  const shown = useRef<HTMLDivElement>(null);
  const [pressed, setPressed] = useState(0);
  const had = state.kind === 'ready';
  // The day shown in full kept in sight in its row, a long stay's late day scrolled to.
  const row = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = row.current;
    const on = strip?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (strip && on) strip.scrollLeft = on.offsetLeft - strip.offsetLeft;
  }, [picked, had]);
  useEffect(() => {
    if (had && (pressed > 0 || reveal)) {
      shown.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [pressed, reveal, had]);
  if (state.kind === 'loading') {
    return <p className="quiet weather-wait">{t('weather.loading')}</p>;
  }
  if (state.kind === 'failed') {
    return <p className="quiet weather-wait">{t('weather.failed')}</p>;
  }
  const { weather } = state;
  const days = weather.stops.find((one) => one.stop === stop)?.days ?? [];
  if (weather.weather === 'off' || days.length === 0) return null;
  const forecast = days.some((one) => one.kind === 'forecast');
  const was = days.some((one) => one.kind === 'was');
  const at = Math.max(
    0,
    days.findIndex((one) => one.day === picked),
  );
  return (
    <section className="stop-weather" aria-labelledby={`weather-${stop}`}>
      <div className="stop-weather-head">
        <h3 id={`weather-${stop}`}>{t('weather.title')}</h3>
        <span className={`state-tag ${forecast || was ? 'state-tag-info' : 'state-tag-plain'}`}>
          {was
            ? t('weather.wasTag')
            : forecast
              ? t('weather.forecastTag')
              : t('weather.usualTag', { from: weather.years[0], to: weather.years[1] })}
        </span>
      </div>
      <div ref={row} className="weather-days" role="group" aria-label={t('weather.days')}>
        {days.map((one, index) => (
          <button
            key={one.day}
            type="button"
            className="weather-day-button"
            aria-pressed={index === at}
            onClick={() => {
              setPicked(one.day);
              setPressed((n) => n + 1);
            }}
          >
            <small>{dayButton(one.day, i18n.language)}</small>
            {one.kind === null ? (
              <span className="quiet">—</span>
            ) : (
              <>
                <SkyIcon sky={one.sky} label={t(`weather.sky.${one.sky}`)} />
                <b>{degrees(one.high)}</b>
                {one.rainChance !== null && (
                  <span className="weather-rain">{String(one.rainChance)} %</span>
                )}
              </>
            )}
          </button>
        ))}
      </div>
      <div ref={shown} className="weather-shown">
        {days.map(
          (one, index) =>
            index === at &&
            (one.kind === null ? (
              <p key={one.day} className="quiet">
                {t('weather.unknown')}
              </p>
            ) : (
              <Detail
                key={one.day}
                day={one}
                forecastDays={weather.forecastDays}
                years={weather.years}
              />
            )),
        )}
      </div>
      {weather.weather === 'failed' && <p className="quiet">{t('weather.partly')}</p>}
    </section>
  );
}
